import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../i18n/I18nProvider";
import { toast } from "sonner";
import { X, Calendar, Leaf, CalendarClock, AlertTriangle, Check } from "lucide-react";
import BookingCalendarGrid from "../BookingCalendarGrid";

const ACTIVE = new Set(["bokad", "godkand", "utlamnad"]);

function isoLocal(d) {
  // Convert a Date object to a "YYYY-MM-DDTHH:mm" local-time string for <input type=datetime-local>
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 16);
}

function plusHoursFromNow(h) {
  const d = new Date();
  d.setHours(d.getHours() + h, 0, 0, 0);
  return isoLocal(d);
}

function fmt(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
}

function fmtDate(d) {
  return d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

export default function MachineBookingModal({ machine, onClose, onCreated }) {
  const { t } = useI18n();
  const [start, setStart] = useState(plusHoursFromNow(1));
  const [slut, setSlut] = useState(plusHoursFromNow(5));
  const [transportKm, setTransportKm] = useState(20);
  const [projectId, setProjectId] = useState("");
  const [note, setNote] = useState("");
  const [projects, setProjects] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [responsibleId, setResponsibleId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const rental = !!machine.enable_rental_reporting;

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => {});
    api.get("/machine-bookings", { params: { machine_id: machine.id } })
      .then((r) => setBookings((r.data || []).filter((b) => ACTIVE.has(b.status))))
      .catch(() => {});
    if (rental) {
      api.get("/org/members").then((r) => setOrgUsers(r.data || [])).catch(() => {});
    }
  }, [machine.id, rental]);

  // Sort active bookings chronologically (upcoming + ongoing only)
  const activeUpcoming = useMemo(() => {
    const now = new Date();
    return bookings
      .filter((b) => new Date(b.slut) > now)
      .sort((a, b) => new Date(a.start) - new Date(b.start));
  }, [bookings]);

  // Earliest free moment after the LAST contiguous block of active bookings starting from now
  const earliestFreeFrom = useMemo(() => {
    if (activeUpcoming.length === 0) return null;
    // Take the end of the latest active booking
    const latestEnd = activeUpcoming.reduce(
      (acc, b) => (new Date(b.slut) > acc ? new Date(b.slut) : acc),
      new Date(0),
    );
    return latestEnd > new Date() ? latestEnd : null;
  }, [activeUpcoming]);

  // Live conflict check based on user's currently-selected start/slut
  const conflict = useMemo(() => {
    if (!start || !slut) return null;
    const s = new Date(start);
    const e = new Date(slut);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()) || e <= s) return null;
    return activeUpcoming.find((b) => {
      const bs = new Date(b.start);
      const be = new Date(b.slut);
      return bs < e && be > s;
    }) || null;
  }, [start, slut, activeUpcoming]);

  const factor = Number(machine.generisk_co2e_transport_per_km || 0.25);
  const co2eKop = Number(machine.generisk_co2e_kop || 0);
  const transport = Math.round(factor * Number(transportKm || 0) * 100) / 100;
  const hyra = Math.round((transport + 1) * 100) / 100;
  const besparing = Math.round((co2eKop - hyra) * 100) / 100;

  const jumpToFreeSlot = () => {
    if (!earliestFreeFrom) return;
    const newStart = new Date(earliestFreeFrom);
    // Snap to next hour
    newStart.setMinutes(0, 0, 0);
    newStart.setHours(newStart.getHours() + 1);
    const newSlut = new Date(newStart.getTime() + 4 * 60 * 60 * 1000);
    setStart(isoLocal(newStart));
    setSlut(isoLocal(newSlut));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (conflict) {
      toast.error(t("mbk.collision"));
      return;
    }
    if (rental && (!projectId || !responsibleId)) {
      toast.error(t("rental.requiredHint"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/machine-bookings", {
        machine_id: machine.id,
        start: new Date(start).toISOString(),
        slut: new Date(slut).toISOString(),
        transport_km: Number(transportKm),
        project_id: projectId || null,
        responsible_user_id: responsibleId || null,
        note,
      });
      toast.success(t("mbk.created"));
      onCreated && onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("mbk.collision"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4 overflow-auto" onClick={onClose} data-testid="machine-booking-modal">
      <div className="bg-[var(--bone)] border border-[var(--ink-200)] max-w-[640px] w-full max-h-[95vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[var(--ink-200)] sticky top-0 bg-[var(--bone)]">
          <div className="font-display text-xl inline-flex items-center gap-2">
            <Calendar size={16} /> {t("mach.bookBtn")} — {machine.namn}
          </div>
          <button onClick={onClose} className="p-1" data-testid="booking-modal-close"><X size={16} /></button>
        </div>

        {/* Booked-slots timeline + earliest-free banner */}
        <div className="p-4 border-b border-[var(--ink-200)]">
          <div className="label-tech inline-flex items-center gap-1 mb-2">
            <CalendarClock size={12} /> {t("mbk.cal.title")}
          </div>

          {/* Visual month calendar — click a day to jump start to 08:00 / slut to 17:00 */}
          <BookingCalendarGrid
            bookings={activeUpcoming}
            onPickDay={(d) => {
              const s = new Date(d); s.setHours(8, 0, 0, 0);
              const e = new Date(d); e.setHours(17, 0, 0, 0);
              setStart(isoLocal(s));
              setSlut(isoLocal(e));
            }}
            className="mb-3"
          />

          {activeUpcoming.length === 0 ? (
            <div
              className="px-3 py-2 border-2 text-sm inline-flex items-center gap-2"
              style={{ borderColor: "var(--moss)", background: "color-mix(in srgb, var(--moss) 12%, var(--surface))" }}
              data-testid="mbk-cal-free"
            >
              <Check size={14} style={{ color: "var(--moss)" }} />
              {t("mbk.cal.allFree")}
            </div>
          ) : (
            <>
              {earliestFreeFrom && (
                <div
                  className="mb-3 px-3 py-2 border-2 flex flex-wrap items-center gap-2"
                  style={{ borderColor: "var(--clay)", background: "color-mix(in srgb, var(--clay) 12%, var(--surface))" }}
                  data-testid="mbk-cal-free-from"
                >
                  <CalendarClock size={14} style={{ color: "var(--clay)" }} />
                  <div className="text-sm flex-1 min-w-0">
                    <span className="font-semibold">{t("mbk.cal.freeFrom")}: </span>
                    <span>{fmtDate(earliestFreeFrom)}</span>
                  </div>
                  <button
                    type="button"
                    onClick={jumpToFreeSlot}
                    className="text-xs px-2 py-1 border border-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--bone)]"
                    data-testid="mbk-cal-jump-btn"
                  >
                    {t("mbk.cal.jumpBtn")}
                  </button>
                </div>
              )}
              <div className="space-y-1.5" data-testid="mbk-cal-list">
                {activeUpcoming.map((b) => (
                  <div
                    key={b.id}
                    className="flex items-center gap-2 px-3 py-1.5 border bg-[var(--surface)]"
                    style={{ borderColor: "var(--clay)" }}
                  >
                    <span
                      className="inline-block w-1.5 h-6"
                      style={{ background: "var(--clay)" }}
                    />
                    <div className="text-xs flex-1">
                      <div className="font-semibold">{fmt(b.start)} → {fmt(b.slut)}</div>
                      <div className="text-[var(--ink-500)]">
                        {b.user_name || b.bokare_namn || "—"}{b.note ? ` · ${b.note.slice(0, 40)}` : ""}
                      </div>
                    </div>
                    <span
                      className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5"
                      style={{ background: "var(--clay)", color: "var(--bone)" }}
                    >
                      {b.status === "utlamnad" ? t("mbk.cal.out") : t("mbk.cal.booked")}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <form onSubmit={submit} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="label-tech mb-2">{t("mbk.start")}</div>
            <input
              type="datetime-local"
              className="input-tech"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              required
              data-testid="booking-start-input"
              style={conflict ? { borderColor: "var(--brick)" } : undefined}
            />
          </div>
          <div>
            <div className="label-tech mb-2">{t("mbk.slut")}</div>
            <input
              type="datetime-local"
              className="input-tech"
              value={slut}
              onChange={(e) => setSlut(e.target.value)}
              required
              data-testid="booking-slut-input"
              style={conflict ? { borderColor: "var(--brick)" } : undefined}
            />
          </div>

          {conflict && (
            <div
              className="md:col-span-2 px-3 py-2 border-2 flex items-center gap-2 text-sm"
              style={{ borderColor: "var(--brick)", background: "color-mix(in srgb, var(--brick) 12%, var(--surface))" }}
              data-testid="mbk-conflict-warn"
            >
              <AlertTriangle size={14} style={{ color: "var(--brick)" }} />
              <span>
                <span className="font-semibold">{t("mbk.cal.conflict")}: </span>
                {fmt(conflict.start)} → {fmt(conflict.slut)}
              </span>
            </div>
          )}

          <div>
            <div className="label-tech mb-2">{t("mbk.transportKm")}</div>
            <input type="number" min="0" step="1" className="input-tech" value={transportKm} onChange={(e) => setTransportKm(e.target.value)} data-testid="booking-km-input" />
          </div>
          <div>
            <div className="label-tech mb-2">{t("prj.selectProject")}{rental && <span className="text-[var(--brick)] ml-1">*</span>}</div>
            <select className="input-tech" value={projectId} onChange={(e) => setProjectId(e.target.value)} required={rental} data-testid="booking-project-select">
              <option value="">—</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.namn}</option>)}
            </select>
          </div>
          {rental && (
            <div data-testid="booking-rental-block">
              <div className="label-tech mb-2 inline-flex items-center gap-1">
                {t("rental.responsible")} <span className="text-[var(--brick)]">*</span>
              </div>
              <select
                className="input-tech"
                value={responsibleId}
                onChange={(e) => setResponsibleId(e.target.value)}
                required
                data-testid="booking-responsible-select"
              >
                <option value="">—</option>
                {orgUsers.map((u) => (
                  <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>
                ))}
              </select>
              <div className="text-[10px] text-[var(--clay)] mt-2 uppercase tracking-wider font-mono-tech">
                {t("rental.willGenerate")}
              </div>
            </div>
          )}
          <div className="md:col-span-2">
            <div className="label-tech mb-2">{t("mbk.note")}</div>
            <textarea rows={2} className="input-tech" value={note} onChange={(e) => setNote(e.target.value)} data-testid="booking-note-input" />
          </div>
          <div className="md:col-span-2 p-4 bg-[var(--moss)] text-[var(--bone)]" data-testid="booking-co2e-preview">
            <div className="label-tech opacity-80 inline-flex items-center gap-2"><Leaf size={12} /> {t("mbk.savings")}</div>
            <div className="font-display text-2xl mt-1">~{besparing} kg CO2e</div>
            <div className="label-tech opacity-80 mt-1">{co2eKop} kg (köp) − {hyra} kg (hyra/transport)</div>
          </div>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={onClose} data-testid="booking-cancel-btn">{t("admin.orgs.cancel")}</button>
            <button
              type="submit"
              disabled={submitting || !!conflict}
              className="btn-primary"
              data-testid="booking-submit-btn"
            >
              {submitting ? "…" : t("mbk.book")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
